import { useState } from "react";

import { api } from "@/api/client";
import { useStore } from "@/state/store";
import { Icon } from "@/components/atoms/Icons";
import { Segmented } from "@/components/atoms/Segmented";

type Asset = "USDC" | "USDT";
type Network = "polygon" | "tron";

export function DepositModal() {
  const open = useStore((s) => s.depositOpen);
  const setOpen = useStore((s) => s.setDepositOpen);
  const userId = useStore((s) => s.userId);
  const showToast = useStore((s) => s.showToast);
  const [asset, setAsset] = useState<Asset>("USDC");
  const [network, setNetwork] = useState<Network>("polygon");
  const [address, setAddress] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const fetchAddress = async () => {
    if (!userId) return;
    setLoading(true);
    const { data, error } = await api.POST("/v1/wallet/deposit-address", {
      body: { asset, network },
    });
    setLoading(false);
    if (error) {
      showToast({ kind: "error", message: "Failed to create address" });
      return;
    }
    if (data) setAddress(data.address);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      <div className="w-[460px] max-w-[92vw] rounded-modal border border-line bg-bg-1 p-6 shadow-[var(--shadow-card)]">
        <div className="flex items-center justify-between">
          <h2 className="text-[18px] font-semibold">Deposit crypto</h2>
          <button
            className="btn-ghost h-9 w-9 rounded-full p-0"
            onClick={() => {
              setOpen(false);
              setAddress(null);
            }}
            aria-label="Close"
          >
            <Icon.X size={16} />
          </button>
        </div>

        <div className="mt-4 flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-2">
            <Segmented<Asset>
              options={[
                { value: "USDC", label: "USDC" },
                { value: "USDT", label: "USDT" },
              ]}
              value={asset}
              onChange={(v) => {
                setAsset(v);
                setAddress(null);
              }}
            />
            <Segmented<Network>
              options={[
                { value: "polygon", label: "Polygon" },
                { value: "tron", label: "Tron" },
              ]}
              value={network}
              onChange={(v) => {
                setNetwork(v);
                setAddress(null);
              }}
            />
          </div>

          {address ? (
            <div className="flex flex-col gap-3">
              <div className="rounded-card border border-line bg-bg-2 p-3 text-center">
                <div className="mb-2 text-[11px] uppercase tracking-wider text-muted">
                  Your {asset} address on {network}
                </div>
                <div className="font-mono text-[12px] break-all text-text">
                  {address}
                </div>
              </div>
              <button
                className="btn"
                onClick={() => {
                  navigator.clipboard.writeText(address);
                  showToast({ kind: "info", message: "Address copied" });
                }}
              >
                <Icon.Copy size={14} />
                Copy address
              </button>
              <div className="rounded-[10px] border border-warn/40 bg-warn/10 px-3 py-2 text-[11.5px] text-warn">
                Send only {asset} on {network}. Other networks/tokens will be lost.
                Wait for 64 confirmations before the balance is credited.
              </div>
            </div>
          ) : (
            <button
              className="btn btn-primary w-full !py-3"
              disabled={loading || !userId}
              onClick={fetchAddress}
            >
              {loading ? "…" : "Get deposit address"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
